import SetMap from './mem/set-map';

type handler = (...args: unknown[]) => unknown;


/**
 * Like the browser's EventDispatcher interface, this EventDispatcher mixin (a
 * class factory function), allows classes to simply and easily handle routing
 * events.
 *
 * If you know the names/types of events ahead of time and you want to enforce
 * typed arguments for event handlers, then you should use the Signal class.
 * If you don't know the types of events at TypeScript compile time, then this
 * EventDispatcher mixin is more appropriate.
 */
export default class EventDispatcher {

    private handlers: SetMap<string, handler> = new SetMap();


    on(type: string, handler: handler) {
        this.handlers.add(type, handler);
    }

    once(type: string, handler: handler) {
        const wrapper = () => {
            handler();
            this.off(type, wrapper);
        }
        this.on(type, wrapper);
    }

    when(type: string) {
        return new Promise((resolve, _reject) => {
            this.once(type, resolve);
        })
    }

    off(type: string, handler?: handler) {
        if(!handler == null){
            this.handlers.delete(type);
        } else {
            this.handlers.deleteValue(type, handler);
        }
    }

    alloff() {
        this.handlers = new SetMap();
    }

    trigger(type: string, ...args: unknown[]) {
        let queue = this.handlers.get(type);
        if(queue){
            let copy: Array<handler> = Array.from(queue);
            for(let handler of copy){
                try {
                    handler(...args);
                } catch(error) {
                    console.error(error);
                }
            }
        }
    }

    /**
     * Making a batch update to an event dispatcher suspends triggering
     * until after the batch is complete. This is emphatically not thread
     * safe, but it's JavaScript, so the EventDispatcher instance should
     * always be thread local. If that ever changes... woe betide.
     */
    batch(fn: () => void) {
        let events = new Array();
        // adds a trigger implementation to this instance only
        this.trigger = (type, ...args) => {
            events.push({type, args});
        }

        fn();

        delete this.trigger; // fall back to the prototype implementation
        for(let {type, args} of events) {
            this.trigger(type, ...args);
        }
    }

    /**
     * Binding registers an event handler for a given type of event and
     * executes that handler immediately. Thus, binding is equivalent to
     * on(..., nowandlater(fn)), where fn is the handler function you'd pass
     * to the on method.
     */
    bind(property: string, handler: handler) {
        this.on(property, handler);
        handler();
    }
}
